import tokens from '@kluein/design-tokens';
import { Box, Button, Text, Tip } from '@kluein/klue-ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import TRACKING_IDS from 'tracking-ids';

import type { SidebarItemProps } from './SidebarItem.types';

const RMBadge = styled(Box)`
  background-color: ${tokens.color.primitives.violet[50]};
  border-radius: ${tokens.borderRadius.small};
  padding: 2px 6px;
`;

export const StyledBorderButton = styled(Box)<{ isActive?: boolean }>`
  transition-property: background-color, color, border;
  transition-duration: 0.3s;
  position: relative;

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    width: ${tokens.borderWidth.small};
    height: 100%;
    background-color: transparent;
  }

  &:hover::before {
    background-color: ${tokens.color.neutral[1000]};
  }

  ${({ isActive }) =>
    isActive &&
    `&::before, &:hover::before {
        background-color: ${tokens.color.primary[800]}
      }`}
`;

export const StyledItemButton = styled(Button)<{ isActive?: boolean }>`
  transition: all 0.3s;
  border-radius: ${tokens.borderRadius.large};

  label {
    font-weight: ${tokens.fontWeight.medium};
    line-height: ${tokens.lineHeight.xdense};
    color: ${tokens.color.neutral[900]};
    letter-spacing: -0.0175rem;
  }
  svg {
    stroke: ${tokens.color.neutral[900]};
    fill: ${tokens.color.neutral[900]};
  }

  &:hover {
    background: ${tokens.color.neutral[300]};
    box-shadow: none;

    label {
      color: ${tokens.color.neutral[1000]};
    }

    svg {
      stroke: ${tokens.color.neutral[1000]};
      fill: ${tokens.color.neutral[1000]};
    }
  }

  ${({ isActive }) =>
    isActive &&
    `, &:hover { 
        background: ${tokens.color.primary[100]};
        svg  {
          fill: ${tokens.color.primary[800]};
          stroke: ${tokens.color.primary[800]};
        }
        label {
          color: ${tokens.color.primary[800]};
        }
      }
    `}
`;

function SidebarItem(props: SidebarItemProps) {
  const { isActive, isRM, handleClick, index, title, sessionId } = props;
  const { t } = useTranslation();

  return (
    <Box flex={{ grow: 1 }} pad={{ left: tokens.spacing.hair }} key={sessionId}>
      <StyledBorderButton fill="horizontal" isActive={isActive} key={index}>
        <Box fill="horizontal" pad={{ horizontal: 'small' }}>
          <StyledItemButton
            label={title}
            isActive={isActive}
            variant="flat-form"
            onClick={() => handleClick(sessionId)}
            data-tracking-id={TRACKING_IDS.askKlue.sidebar.item}
            rightSlot={
              isRM ? (
                <Tip
                  content={t(
                    'Dashboard:askKlue.answerModal.sidebar.researchMode',
                  )}
                  dropProps={{ align: { bottom: 'top' } }}
                  color="dark"
                  variant="small"
                >
                  <Box flex={{ grow: 1 }} align="end">
                    <RMBadge>
                      <Text
                        size="xsmall"
                        color={tokens.color.primitives.violet[500]}
                        fontWeight="semibold"
                      >
                        RM
                      </Text>
                    </RMBadge>
                  </Box>
                </Tip>
              ) : undefined
            }
            title={title}
          />
        </Box>
      </StyledBorderButton>
    </Box>
  );
}

export default SidebarItem;
